import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils.js';
import { trackGtmClick } from './gtm';

// Route local development requests through Vite so browser proxy/CORS settings
// cannot block the PC login preflight. Built deployments use their configured API.
const API_BASE = (
  import.meta.env.DEV && import.meta.env.MODE === 'development'
    ? '/__api'
    : import.meta.env.VITE_APP_BASE_API
).replace(/\/$/, '');
const H5_VERSION = '1.0.0';
const DEVICE_ID_KEY = 'koemori-h5-device-id';
const SESSION_KEY = 'koemori-h5-session';
const SESSION_INIT_URI = '/h5/session/init';
const TRIAL_APPLY_URI = '/h5/trial/apply/submit';

interface ApiEnvelope<T> {
  code: number | string;
  msg?: string;
  data: T;
}

interface H5SessionResponse {
  appKey: string;
  secretKey: string;
  expireTime: number | string;
  expiresIn?: number | string;
}

interface H5Session {
  appKey: string;
  secretKey: string;
  expireTime: number;
}

export interface TrialApplication {
  nickname: string;
  email: string;
}

class H5ApiError extends Error {
  code: number | null;

  constructor(message: string, code: number | null = null) {
    super(message);
    this.name = 'H5ApiError';
    this.code = code;
  }
}

let memoryDeviceId = '';
let memorySession: H5Session | null = null;

const createUuid = () => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
};

const getDeviceId = () => {
  try {
    const stored = window.localStorage.getItem(DEVICE_ID_KEY);
    if (stored) return stored;
    const created = createUuid();
    window.localStorage.setItem(DEVICE_ID_KEY, created);
    return created;
  } catch {
    if (!memoryDeviceId) memoryDeviceId = createUuid();
    return memoryDeviceId;
  }
};

const readStoredSession = (): H5Session | null => {
  try {
    const stored = window.localStorage.getItem(SESSION_KEY);
    if (!stored) return memorySession;
    const parsed = JSON.parse(stored) as H5Session;
    if (!parsed.appKey || !parsed.secretKey || !Number.isFinite(parsed.expireTime)) return null;
    return parsed;
  } catch {
    return memorySession;
  }
};

const storeSession = (session: H5Session) => {
  memorySession = session;
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // In-memory storage keeps the form usable when localStorage is unavailable.
  }
};

const clearSession = () => {
  memorySession = null;
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore storage restrictions; a fresh session will be used in memory.
  }
};

const commonHeaders = (timestamp: string, nonce: string) => ({
  'Content-Type': 'application/json',
  'Uttm-Client-Platform': 'h5',
  'Uttm-Device-Id': getDeviceId(),
  'Uttm-Version': H5_VERSION,
  Timestamp: timestamp,
  Nonce: nonce,
});

const parseResponse = async <T>(response: Response): Promise<ApiEnvelope<T>> => {
  let payload: ApiEnvelope<T>;
  try {
    payload = await response.json() as ApiEnvelope<T>;
  } catch {
    throw new H5ApiError('一時的なエラーが発生しました。時間をおいて再度お試しください。');
  }

  if (!response.ok && Number(payload.code) === 0) {
    throw new H5ApiError('通信に失敗しました。時間をおいて再度お試しください。');
  }
  return payload;
};

const initSession = async (signal?: AbortSignal): Promise<H5Session> => {
  const timestamp = Date.now().toString();
  const nonce = createUuid();
  const response = await fetch(API_BASE + SESSION_INIT_URI, {
    method: 'POST',
    headers: commonHeaders(timestamp, nonce),
    body: '{}',
    signal,
  });
  const payload = await parseResponse<H5SessionResponse>(response);
  const code = Number(payload.code);
  if (code !== 0 || !payload.data?.appKey || !payload.data?.secretKey) {
    throw new H5ApiError(payload.msg || 'セッションの初期化に失敗しました。', code);
  }

  const serverExpireTime = Number(payload.data.expireTime);
  const expiresIn = Number(payload.data.expiresIn || 0);
  const expireTime = Number.isFinite(serverExpireTime) && serverExpireTime > Date.now()
    ? serverExpireTime
    : Date.now() + expiresIn * 1000;
  const session = {
    appKey: payload.data.appKey,
    secretKey: payload.data.secretKey,
    expireTime,
  };
  storeSession(session);
  return session;
};

const getSession = async (forceRefresh = false, signal?: AbortSignal) => {
  if (!forceRefresh) {
    const stored = readStoredSession();
    if (stored && stored.expireTime > Date.now() + 30_000) return stored;
  }
  clearSession();
  return initSession(signal);
};

const decodeBase64 = (value: string) => {
  const binary = window.atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

const encodeBase64 = (value: Uint8Array) => {
  let binary = '';
  value.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary);
};

const createSignature = (
  session: H5Session,
  rawBody: string,
  timestamp: string,
  nonce: string,
  uri: string,
) => {
  const bodySha256 = bytesToHex(sha256(utf8ToBytes(rawBody)));
  const signingText = [
    'App-Key=' + session.appKey,
    'Nonce=' + nonce,
    'Timestamp=' + timestamp,
    'Uttm-Client-Platform=h5',
    'Uttm-Device-Id=' + getDeviceId(),
    'Uttm-Version=' + H5_VERSION,
    'bodySha256=' + bodySha256,
    'method=POST',
    'uri=' + uri,
  ].join('&');
  const signature = hmac(sha256, decodeBase64(session.secretKey), utf8ToBytes(signingText));
  return encodeBase64(signature);
};

const wait = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export const submitTrialApplication = async (application: TrialApplication) => {
  trackGtmClick({
    event_category: 'trial',
    event_action: 'apply_submit',
    event_label: 'collect_click',
  });

  const rawBody = JSON.stringify({
    nickname: application.nickname,
    email: application.email,
  });
  let session = await getSession();
  let refreshedExpiredSession = false;
  let retriedRateLimit = false;

  while (true) {
    const timestamp = Date.now().toString();
    const nonce = createUuid();
    const sign = createSignature(session, rawBody, timestamp, nonce, TRIAL_APPLY_URI);
    const response = await fetch(API_BASE + TRIAL_APPLY_URI, {
      method: 'POST',
      headers: {
        ...commonHeaders(timestamp, nonce),
        'App-Key': session.appKey,
        Sign: sign,
      },
      body: rawBody,
    });
    const payload = await parseResponse<unknown>(response);
    const code = Number(payload.code);

    if (code === 0) return;
    if (code === 911109 && !refreshedExpiredSession) {
      refreshedExpiredSession = true;
      session = await getSession(true);
      continue;
    }
    if (code === 911112 && !retriedRateLimit) {
      retriedRateLimit = true;
      await wait(1000);
      continue;
    }
    if (code === 911108) clearSession();
    throw new H5ApiError(payload.msg || '応募の送信に失敗しました。', code);
  }
};

// PC endpoints reuse the site's signed /h5 transport until the server
// confirms a different method or authenticated-header convention.
export const postH5Json = async <T>(uri: string, body: unknown, accessToken?: string, signal?: AbortSignal): Promise<T> => {
  const rawBody = JSON.stringify(body ?? {});
  let session: H5Session;
  signal?.throwIfAborted();
  try { session = await getSession(false, signal); }
  catch (reason) {
    if (reason instanceof TypeError) throw new H5ApiError('サーバーに接続できません。インターネットの接続状況をご確認ください。');
    throw reason;
  }
  let refreshedSession = false;
  let retriedRateLimit = false;

  while (true) {
    signal?.throwIfAborted();
    const timestamp = Date.now().toString();
    const nonce = createUuid();
    let response: Response;
    try {
      response = await fetch(API_BASE + uri, {
        method: 'POST',
        headers: {
          ...commonHeaders(timestamp, nonce),
          'App-Key': session.appKey,
          Sign: createSignature(session, rawBody, timestamp, nonce, uri),
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: rawBody,
        signal,
      });
    } catch (reason) {
      if (reason instanceof TypeError) throw new H5ApiError('サーバーに接続できません。インターネットの接続状況をご確認ください。');
      throw reason;
    }
    let payload: unknown;
    try {
      const responseText = await response.text();
      if (!responseText.trim()) {
        if (response.ok) return undefined as T;
        throw new H5ApiError('通信に失敗しました。時間をおいて再度お試しください。');
      }
      payload = JSON.parse(responseText);
    } catch (reason) {
      if (reason instanceof H5ApiError) throw reason;
      throw new H5ApiError('データの取得に失敗しました。しばらくしてから再度お試しください。');
    }
    if (!payload || typeof payload !== 'object') throw new H5ApiError('データの読み込みに失敗しました。再度お試しください。');
    const result = payload as Record<string, unknown>;
    if ('code' in result) {
      const code = Number(result.code);
      if (code === 0 && response.ok) return result.data as T;
      if (code === 911109 && !refreshedSession) {
        refreshedSession = true;
        session = await getSession(true, signal);
        continue;
      }
      if (code === 911112 && !retriedRateLimit) {
        retriedRateLimit = true;
        await wait(1000);
        continue;
      }
      if (code === 911108) clearSession();
      throw new H5ApiError(String(result.msg || '通信に失敗しました。時間をおいて再度お試しください。'), code);
    }
    if (!response.ok) throw new H5ApiError('通信に失敗しました。時間をおいて再度お試しください。');
    return result as T;
  }
};
